import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

// Phase 3 (F6): the external-adviser shell.
//
// A deliberately minimal chrome that exposes ONLY the four surfaces an external adviser is
// entitled to (blog_public, podcast, insight_published, portfolio_marketing) — no research
// nav, no surface tabs, no ops console. It is mounted under routes carrying `allowAdviser`
// on <ProtectedRoute>, so it can only render for a confirmed adviser (or a staff member, who
// falls through the normal gates). Until `adviser_links` has data, no principal reaches it.

type Tab = { to: string; label: string };

const TABS: Tab[] = [
  { to: "/adviser/insights", label: "Insights" },
  { to: "/adviser/blog", label: "Commentary" },
  { to: "/adviser/podcasts", label: "Podcasts" },
  { to: "/adviser/portfolios", label: "Portfolios" },
];

export default function AdviserLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/adviser/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-4 flex-wrap">
          <div className="flex items-center gap-3">
            <img
              src="/images/cbam-logo.png"
              alt="CBAM Asset Management"
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-lg font-bold leading-tight">Adviser Portal</h1>
              <p className="text-xs text-muted-foreground">
                Published research, commentary and portfolio materials
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.email && (
              <span className="hidden sm:inline text-sm text-muted-foreground">{user.email}</span>
            )}
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
        <nav className="container mx-auto flex gap-1 flex-wrap px-4 pb-2">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
